// Render Handbook-style names ("KATTER, the Hon. Robert Cummin") as readable
// display names ("the Hon. Robert Katter"), with surname title-casing that
// respects Mc/Mac/O'/hyphenated/multi-word surnames. Falls back to DisplayName.
//
// Quick self-test (node -e, run from frontend/src):
//   titleCaseSurname('KATTER')      === 'Katter'
//   titleCaseSurname('McGOWAN')     === 'McGowan'
//   titleCaseSurname("O'NEILL")     === "O'Neill"
//   titleCaseSurname('VAN MANEN')   === 'Van Manen'
//   titleCaseSurname('SMITH-JONES') === 'Smith-Jones'
//   formatParliamentarianName({ DisplayName: 'KATTER, the Hon. Robert Cummin',
//     FamilyName: 'KATTER', GivenName: 'Robert', MiddleNames: 'Cummin',
//     PreferredName: 'Bob' }) === 'the Hon. Robert (Bob) Katter'
//   ...same with PreferredName: '(Bob)' or ' (Bob) '  → 'the Hon. Robert (Bob) Katter' (no double wrap)
//   ...GivenName: 'Robert (Bob)', PreferredName: '(Bob)' → 'the Hon. Robert (Bob) Katter'

// Title-case one whitespace/hyphen/apostrophe-free word, keeping the Mc
// internal capital ("McGOWAN" / "MCGOWAN" -> "McGowan"). "Mac" is only
// honoured when the source already shows it mixed-case ("MacDONALD" ->
// "MacDonald") because all-caps MACKLIN / MACKAY are ambiguous.
function caseWord(w) {
  if (!w) return w;
  const lower = w.toLowerCase();
  if (lower.length > 2 && lower.startsWith('mc')) {
    return 'Mc' + lower.charAt(2).toUpperCase() + lower.slice(3);
  }
  if (lower.length > 3 && w.startsWith('Mac')) {
    return 'Mac' + lower.charAt(3).toUpperCase() + lower.slice(4);
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function titleCaseSurname(s) {
  if (!s) return s;
  // Already readable (no run of two capitals, e.g. "de Groot", "McGowan",
  // "O'Neill")? Leave it alone.
  if (!/[A-Z]{2}/.test(s)) return s;
  // Split on spaces, hyphens and apostrophes but keep the separators so the
  // surname is reassembled exactly ("O'NEILL" -> "O'Neill", "SMITH-JONES" -> "Smith-Jones").
  return s
    .split(/([\s\-'’])/)
    .map((part) => (/^[\s\-'’]$/.test(part) ? part : caseWord(part)))
    .join('');
}

export function formatParliamentarianName(p, { includeMiddle = false } = {}) {
  if (!p) return '';
  const display = p.DisplayName || '';
  const family = (p.FamilyName || '').trim();
  const given = (p.GivenName || '').trim();
  if (!family || !given) return display;

  // Honorific = text in DisplayName after the first ", " and before GivenName.
  let honorific = '';
  const comma = display.indexOf(', ');
  if (comma !== -1) {
    const rest = display.slice(comma + 2);
    const gi = rest.indexOf(given);
    honorific = (gi === -1 ? '' : rest.slice(0, gi)).trim();
  }

  // The API's PreferredName sometimes already carries parentheses ("(Bob)");
  // normalise so "(Bob)", "Bob" and " (Bob) " all yield "(Bob)" exactly once,
  // and never add a parenthetical when GivenName already contains one.
  const preferred = (p.PreferredName || '').trim().replace(/^\(+\s*|\s*\)+$/g, '').trim();
  const addPreferred = preferred && preferred !== given && !given.includes('(');
  const givenPart = addPreferred ? `${given} (${preferred})` : given;
  const middle = includeMiddle ? (p.MiddleNames || '').trim() : '';

  return [honorific, givenPart, middle || null, titleCaseSurname(family)]
    .filter(Boolean)
    .join(' ');
}

export default formatParliamentarianName;
